import { aiDocumentIntakeApi } from 'app/services/api';

import {
  DOCUMENT_INTAKE_ACTION_TYPE,
  DOCUMENT_INTAKE_POLL_INTERVAL_MS,
  DocumentIntakeJobStatus,
  DocumentIntakeTerminalStatuses,
} from 'constants/documentIntake';
import { catchAsyncActionError } from 'utils/action';

import { showErrorMessageAction, showMessageAction } from 'store/global/actions';

import { hasUnfiledSegments, selectActiveJobIdForLoan } from '../selectors';

export const jobUpsertAction = job => ({
  type: DOCUMENT_INTAKE_ACTION_TYPE.JOB_UPSERT,
  payload: { job },
});

const setActiveJobForLoanAction = ({ loanApplicationId, jobId }) => ({
  type: DOCUMENT_INTAKE_ACTION_TYPE.SET_ACTIVE_JOB_FOR_LOAN,
  payload: { loanApplicationId, jobId },
});

export const clearActiveJobForLoanAction = ({ loanApplicationId }) => ({
  type: DOCUMENT_INTAKE_ACTION_TYPE.CLEAR_ACTIVE_JOB_FOR_LOAN,
  payload: { loanApplicationId },
});

/**
 * Submit one or more files for background intake. Returns the created job on
 * success. Throws on failure so the call site (AiBatchIntake.processFiles) can
 * handle the error toast — this prevents the double-toast that
 * `catchAsyncActionError` would otherwise cause.
 *
 * DEV-4526: `uploadLabel` is derived from the file list by the call site and
 * stored on CREATE_JOB_SUCCESS so the IntakeStatusCard can display
 * "Processing <label>" / "Ready to review — <label>" without a second network call.
 *
 * @param {{ files: File[], loanApplicationId: string, uploadLabel: string }} params
 */
export const createIntakeJobAction =
  ({ files, loanApplicationId, uploadLabel }) =>
  async dispatch => {
    dispatch({
      type: DOCUMENT_INTAKE_ACTION_TYPE.CREATE_JOB,
      payload: { loanApplicationId },
    });

    try {
      const job = await aiDocumentIntakeApi.createJob({ files, loanApplicationId });

      dispatch({
        type: DOCUMENT_INTAKE_ACTION_TYPE.CREATE_JOB_SUCCESS,
        payload: { job, loanApplicationId, uploadLabel },
      });

      dispatch(setActiveJobForLoanAction({ loanApplicationId, jobId: job.id }));

      return job;
    } catch (err) {
      dispatch({
        type: DOCUMENT_INTAKE_ACTION_TYPE.CREATE_JOB_FAILURE,
        payload: { loanApplicationId },
      });
      throw err;
    }
  };

const loadJobsForLoanAction = catchAsyncActionError(({ loanApplicationId }) => async dispatch => {
  dispatch({
    type: DOCUMENT_INTAKE_ACTION_TYPE.LOAD_JOBS_FOR_LOAN,
    payload: { loanApplicationId },
  });

  const jobs = await aiDocumentIntakeApi.listJobs({ loanApplicationId, mineOnly: true });

  dispatch({
    type: DOCUMENT_INTAKE_ACTION_TYPE.LOAD_JOBS_FOR_LOAN_SUCCESS,
    payload: { loanApplicationId, jobs },
  });

  return jobs;
});

export const cancelIntakeJobAction = catchAsyncActionError(
  ({ id, loanApplicationId }) =>
    async dispatch => {
      dispatch({ type: DOCUMENT_INTAKE_ACTION_TYPE.CANCEL_JOB, payload: { id } });

      await aiDocumentIntakeApi.cancelJob({ id });

      const updatedJob = await aiDocumentIntakeApi.getJob({ id });

      dispatch({
        type: DOCUMENT_INTAKE_ACTION_TYPE.CANCEL_JOB_SUCCESS,
        payload: { job: updatedJob },
      });

      if (loanApplicationId) {
        dispatch(clearActiveJobForLoanAction({ loanApplicationId }));
      }
    },
);

/**
 * Poll a job until it reaches a terminal status, then stop.
 * Used when SignalR is unavailable or as a baseline guarantee.
 * On terminal transition, dispatches a success/error toast so users who
 * navigated away are notified. The `wasTerminal` guard prevents re-firing
 * on a reconnect poll when the job is already terminal in the store.
 * @param {{ id: string, intervalMs?: number }} params
 * @returns {() => void} clearPoll function
 */
export const startIntakePollAction =
  ({ id, intervalMs }) =>
  (dispatch, getState) => {
    const intervalRef = { handle: null };

    const poll = async () => {
      const job = await aiDocumentIntakeApi.getJob({ id }).catch(() => null);

      if (!job) {
        return;
      }

      const prevJob = getState().documentIntake?.jobsById?.[job.id];
      const wasTerminal = !!prevJob && DocumentIntakeTerminalStatuses.has(prevJob.status);

      dispatch(jobUpsertAction(job));

      if (DocumentIntakeTerminalStatuses.has(job.status)) {
        clearInterval(intervalRef.handle);

        if (!wasTerminal) {
          if (job.status === DocumentIntakeJobStatus.COMPLETED) {
            const count = job.totalSegments ?? 0;

            dispatch(
              showMessageAction({
                message: `Document intake ready — ${count} document${
                  count === 1 ? '' : 's'
                } to review`,
                variant: 'success',
              }),
            );
          }

          if (job.status === DocumentIntakeJobStatus.FAILED) {
            dispatch(
              showErrorMessageAction({
                message: 'Document intake failed — please try again',
              }),
            );
          }
        }
      }
    };

    poll();
    intervalRef.handle = setInterval(poll, intervalMs ?? DOCUMENT_INTAKE_POLL_INTERVAL_MS);

    return () => clearInterval(intervalRef.handle);
  };

/**
 * Initialise tracking for a loan: load existing active jobs and set the
 * most-recent active one as the "active job" for the loan tab status surface.
 *
 * DEV-4598: also resurfaces the most-recent Completed job that still has at
 * least one un-filed segment so a hard reload does not lose the "Ready to
 * review" card. Fully-filed (all segments = "Filed") and user-dismissed
 * (activeJobId already cleared) jobs are NOT resurfaced.
 *
 * Priority: non-terminal (Pending / InProgress) job wins over Completed
 * because an in-progress upload is always the more urgent surface.
 */
export const initIntakeForLoanAction = catchAsyncActionError(
  ({ loanApplicationId }) =>
    async (dispatch, getState) => {
      const jobs = await dispatch(loadJobsForLoanAction({ loanApplicationId }));

      if (!Array.isArray(jobs)) {
        return;
      }

      const existing = selectActiveJobIdForLoan(loanApplicationId)(getState());

      if (existing) {
        return;
      }

      const latestActive = jobs.find(j => !DocumentIntakeTerminalStatuses.has(j.status));

      if (latestActive) {
        dispatch(setActiveJobForLoanAction({ loanApplicationId, jobId: latestActive.id }));

        return;
      }

      // DEV-4598: after a hard reload there is no in-flight job, but the most-recent
      // Completed job may still have segments the user never filed. Surface it so the
      // review drawer stays reachable without requiring a re-upload.
      const latestCompletedUnfiled = jobs.find(
        j => j.status === DocumentIntakeJobStatus.COMPLETED && hasUnfiledSegments(j),
      );

      if (latestCompletedUnfiled) {
        dispatch(
          setActiveJobForLoanAction({ loanApplicationId, jobId: latestCompletedUnfiled.id }),
        );
      }
    },
);
